import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {select, Store} from '@ngrx/store';
import {take} from 'rxjs/operators';
import {Product, ProductDictionary} from '../../../products/models/product';
import {ConfirmationDialogComponent} from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import {Country, CountryDictionary} from '../../model/country';
import {Quote, QuoteConstants, QuoteItem, QuoteRequest, QuoteState} from '../../model/quote';
import {QuoteEditProductCommand, QuoteProductCommand} from '../../model/quote-commands';
import {AbstractQuoteComponent} from '../abstract-quote/abstract-quote.component';
import * as fromQuote from '../../reducers/index';
import * as QuoteActions from '../../actions/quote.action';
import {Observable} from 'rxjs';
import * as fromUser from '../../../user/reducers';
import {User} from 'src/app/user/models/user';
import { StoreService } from 'src/app/core/services/store.service';


@Component({
    selector: 'app-quote-detail',
    templateUrl: './quote-detail.component.html',
    styleUrls: ['./quote-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuoteDetailComponent extends AbstractQuoteComponent implements OnInit{
    readonly deleteQuoteMessageTextId = 'quote-detail_delete-quote-confirmation-message';
    readonly deleteQuoteProductMessageTextId = 'quote-detail_delete-quote-product-confirmation-message';

    isOrder: boolean;
    quoteToRequest: Quote;
    requestQuote$: Observable<boolean>;
    countriesIsPending$: Observable<boolean>;
    countries$: Observable<Country[]>;
    userData$: Observable<User>;
    editQuote$: Observable<boolean>;
    quoteName: string;

    @Input()
    productDictionary: ProductDictionary;

    @Input()
    countryDictionary: CountryDictionary;

    @Input()
    productsIsLoading: boolean;

    @Input()
    selectedQuote: Quote;

    @Output()
    deselectQuote = new EventEmitter<void>();

    @Output()
    deleteQuoteProduct = new EventEmitter<QuoteProductCommand>();

    @Output()
    editQuoteProduct = new EventEmitter<QuoteEditProductCommand>();

    @Output()
    closeQuoteList = new EventEmitter();

    quantityForms: { [productId: number]: FormGroup } = {};

    readonly quantityMinValue = QuoteConstants.PRODUCT_QUANTITY_MIN_VALUE;
    readonly quantityMaxValue = QuoteConstants.PRODUCT_QUANTITY_MAX_VALUE;
    readonly decorDefaultName = QuoteConstants.DECOR_DEFAULT_NAME;
    readonly quoteStateNew = QuoteState.NEW;

    tooltipMessage = $localize`:@@quote-detail_tooltip-message:To receive a personalized quote for the products you've selected, please click on 'Request'. If you're ready to proceed with the order, click on 'Order' to place it.`;

    constructor(protected dialog: MatDialog, private store: Store<fromQuote.State>, private userStore: Store<fromUser.AuthState>, public storeService: StoreService) {
        super();
    }

    ngOnInit(): void {
        this.requestQuote$ = this.store.pipe(select(fromQuote.getRequestQuote));
        this.countriesIsPending$ = this.store.pipe(select(fromQuote.getCountriesIsPending));
        this.countries$ = this.store.pipe(select(fromQuote.getCountriesArray));
        this.userData$ = this.userStore.pipe(select(fromUser.getUser));
        this.editQuote$ = this.store.pipe(select(fromQuote.getQuoteEdit));

        if (this.quote.name.length > 18) {
            this.quoteName = `${this.quote.name.split("").splice(18).join("")}...`;
        } else {
            this.quoteName = this.quote.name;
        }

        this.onQuoteBarItemChanged();
    }

    get country(): Country {
        if (this.hasRequestData && this.countryDictionary != null) {
            return this.countryDictionary[this.requestAddress.countryId];
        }
        return null;
    }

    quantityControl(productId: number) {
        return this.quantityForms[productId].get('quantity');
    }

    onDeselectQuote() {
        this.deselectQuote.emit();
    }

    hasThumbnail(productId: number): boolean {
        return this.productDictionary[productId].thumbnailUrl !== null;
    }
    thumbnail(productId: number) {
        return this.productDictionary[productId].thumbnailUrl;
    }

    getTotalPrice() {
        let price: number = 0;
        this.quote.quoteItems.forEach((quoteItemInfo) => {
            price += (this.productDictionary[quoteItemInfo.productId].price * quoteItemInfo.quantity);
        })
        return price;
    }
    closeDetail() {
        this.store.dispatch(QuoteActions.closeQuoteListMenu());
    }

    openDeleteProductConfirmationDialog(product: Product): void {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: `${this.dialogWidthPx}px`,
            data: {
                subheader: `${this.quote.name} - ${product.name}`,
                message: this.i18nDictionary.get(this.deleteQuoteProductMessageTextId)
            }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
            if (result === true) {
                this.deleteQuoteProduct.emit({quote: this.quote, product: product});
            }
        });
    }

    openRequestQuote(quote: Quote, isOrder: boolean): void {
        this.isOrder = isOrder;
        this.quoteToRequest = quote;
        this.store.dispatch(QuoteActions.openRequestQuote());

    }

    editQuote() {
        this.store.dispatch(QuoteActions.openEditQuote());
    }

    reduceQuantity(quoteItem: QuoteItem) {
        if(this.quantityForms[quoteItem.productId].get('quantity').value > QuoteConstants.PRODUCT_QUANTITY_MIN_VALUE && this.quote.state === QuoteState.NEW) {
            this.quantityForms[quoteItem.productId].get('quantity').setValue(this.quantityControl(quoteItem.productId).value - 1);
            this.onEditQuantity(quoteItem);
        }
    }
    increaseQuantity(quoteItem: QuoteItem) {
        if(this.quantityForms[quoteItem.productId].get('quantity').value < QuoteConstants.PRODUCT_QUANTITY_MAX_VALUE && this.quote.state === QuoteState.NEW)
        this.quantityForms[quoteItem.productId].get('quantity').setValue(this.quantityControl(quoteItem.productId).value + 1);
        this.onEditQuantity(quoteItem);
    }

    onEditQuantity(quoteItem: QuoteItem) {
        const form = this.quantityForms[quoteItem.productId];
        if (!form.valid) {
            return;
        }
        const currentQuantity = quoteItem.quantity;
        const newQuantity = this.quantityControl(quoteItem.productId).value;
        if (currentQuantity === newQuantity) {
            console.log('The quantity did not change. QuoteItem won\'t be edited.');
            return;
        }

        this.editQuoteProduct.emit({
            quantity: newQuantity,
            quote: this.quote,
            product: this.productDictionary[quoteItem.productId]
        });
    }

    onCloseQuoteList() {
        this.closeQuoteList.emit();
    }

    onDownloadQuote() {
        this.store.dispatch(QuoteActions.download({quote: this.quote}));
    }

    protected onQuoteBarItemChanged(): void {
        if (this.quoteBarItem.isPending) {
            return;
        }

        if (this.quoteBarItem.quote != undefined) {
            this.quoteName = this.quoteBarItem.quote.name;
        }

        for (const quoteItem of this.selectedQuote.quoteItems) {
            this.quantityForms[quoteItem.productId] = new FormGroup({
                    quantity: new FormControl(quoteItem.quantity, [
                        Validators.min(this.quantityMinValue),
                        Validators.max(this.quantityMaxValue)
                    ])
                }
            );
        }
    }
}
